import DOMPurify from 'dompurify';

/**
 * 内容富文本白名单清洗（渲染层二次防御，存储层由后端 bleach 兜底）
 *
 * 允许的标签：结构 + 段落 + 列表 + 引用 + 代码 + 图片 + 媒体 + 表格
 * 禁止：script / iframe / object / embed / link / meta / style / base / form
 *       以及所有 on* 事件钩子 / 外部协议
 *
 * ⚠️ 2026-09-18 变更：**放行 style 属性**。
 *   此前 FORBID_ATTR 含 'style'，导致编辑器里设置的文字颜色、对齐、图片自适应
 *   （max-width）在用户端被全部剥掉 —— 「编辑器里设了、用户端看不到」。
 *   放行后由 DOMPurify 自行过滤 style 中的危险取值（expression / javascript: 等），
 *   且后端存储阶段已按「属性白名单 + 值级兜底」清洗过一遍。
 *
 * 适用范围：科普文章正文（管理端预览 + 用户端详情）、礼品详情
 *
 * 注意：此函数仅做前端二次防御，后端在保存 / 输出阶段也应当做等价清洗。
 */
const ALLOWED_TAGS = [
  'a', 'p', 'br', 'hr', 'span', 'div',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'strong', 'em', 'b', 'i', 'u', 's', 'mark', 'small', 'sub', 'sup', 'del',
  'ul', 'ol', 'li',
  'blockquote', 'pre', 'code',
  'img', 'figure', 'figcaption',
  'video', 'audio', 'source', 'track',
  'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td', 'caption', 'col', 'colgroup',
  /* 公众号导入（2026-09-19）：公众号排版全靠 section 嵌套，不放行整个版式会被剥光。
     section/header/footer/article 只是普通容器，无脚本能力，放行安全
     （与后端 content_service._ALLOWED_TAGS 保持同一口径）。 */
  'section', 'header', 'footer', 'article',
];

const ALLOWED_ATTR = [
  'href', 'title', 'target', 'rel',
  'src', 'alt', 'width', 'height', 'loading',
  'class', 'id', 'style',
  'controls', 'preload', 'poster', 'type',
  'colspan', 'rowspan', 'scope',
  'border', 'cellpadding', 'cellspacing', 'span',
  'data-color', 'data-src',
  'datetime',
];

const FORBID_TAGS = ['script', 'iframe', 'object', 'embed', 'link', 'meta', 'style', 'base', 'form'];

/** 仅拦截事件钩子；style 已放行（见文件头说明） */
const FORBID_ATTR = [
  'onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur',
  'onmouseenter', 'onmouseleave', 'oninput', 'onchange', 'onsubmit', 'onanimationstart',
];

export function sanitizeHtml(input: string | null | undefined): string {
  if (!input) return '';
  const cleaned = DOMPurify.sanitize(input, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    FORBID_TAGS,
    FORBID_ATTR,
    USE_PROFILES: { html: true },
  });
  return stripUnsafeStyleValues(stripDataUris(cleaned));
}

/**
 * DOMPurify 默认**不清洗 style 属性的取值**（只按标签/属性名过滤）。
 * 实测：`style="width:expression(alert(1))"`、`style="background-image:url(javascript:...)"`
 * 都会被原样放行。此处按声明逐条过滤，与后端 `_scrub_style_values` 保持同一口径。
 */
const DANGEROUS_CSS_RE = /(expression\s*\(|javascript\s*:|vbscript\s*:|url\s*\(|@import|behavior\s*:|-moz-binding)/i;

function stripUnsafeStyleValues(html: string): string {
  return html.replace(/\sstyle="([^"]*)"/gi, (_m, value: string) => {
    const kept = String(value)
      .split(';')
      .map((d) => d.trim())
      .filter((d) => d && !DANGEROUS_CSS_RE.test(d));
    return kept.length ? ` style="${kept.join('; ')}"` : '';
  });
}

/**
 * DOMPurify 默认允许 img/audio/video/source/track 上的 data: URI。
 * 后端存储阶段会直接剔除（sanitize_rich_text），此处保持同一口径，
 * 避免历史或旁路写入的 base64 图片绕过上传通道渲染出来。
 */
function stripDataUris(html: string): string {
  return html.replace(/\s(src|href)="(?:data|javascript|vbscript):[^"]*"/gi, '');
}

/** 判断富文本是否为空（wangEditor 空内容为 `<p><br></p>`） */
export function isBlankHtml(input: string | null | undefined): boolean {
  if (!input) return true;
  return !input
    .replace(/<br\s*\/?>/gi, '')
    .replace(/&nbsp;/gi, '')
    .replace(/<[^>]*>/g, '')
    .trim();
}
